import { useState, useEffect } from 'react'
import {
  FileText,
  Calendar,
  Users,
  Target,
  TrendingUp,
  Clock,
  MapPin,
  User,
  ChevronLeft,
  ChevronRight,
  Download,
  RefreshCw,
  BarChart3,
  ArrowUp,
  ArrowDown
} from 'lucide-react'
import {
  getDailyReport,
  getWeeklyReport,
  getMemberReport,
  getReportSummary,
  getMembers
} from '../../services/fieldTeamApi'
import './Reports.css'

export default function Reports() {
  const [view, setView] = useState('summary') // summary, daily, weekly, member
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // Data states
  const [summaryData, setSummaryData] = useState(null)
  const [dailyData, setDailyData] = useState(null)
  const [weeklyData, setWeeklyData] = useState(null)
  const [memberData, setMemberData] = useState(null)

  // Filter states
  const [summaryPeriod, setSummaryPeriod] = useState('30')
  const [dailyDate, setDailyDate] = useState(new Date().toISOString().split('T')[0])
  const [weekStart, setWeekStart] = useState(getMonday(new Date()))
  const [selectedMember, setSelectedMember] = useState(null)
  const [members, setMembers] = useState([])

  // Get Monday of current week
  function getMonday(date) {
    const d = new Date(date)
    const day = d.getDay()
    const diff = d.getDate() - day + (day === 0 ? -6 : 1)
    return new Date(d.setDate(diff)).toISOString().split('T')[0]
  }

  // Fetch members for dropdown
  useEffect(() => {
    getMembers()
      .then(res => {
        if (res.data.success) {
          setMembers(res.data.data)
        }
      })
      .catch(console.error)
  }, [])

  // Fetch data based on view
  useEffect(() => {
    fetchData()
  }, [view, summaryPeriod, dailyDate, weekStart, selectedMember])

  const fetchData = async () => {
    setLoading(true)
    setError(null)

    try {
      switch (view) {
        case 'summary':
          const summaryRes = await getReportSummary({ period: summaryPeriod })
          if (summaryRes.data.success) {
            setSummaryData(summaryRes.data.data)
          }
          break

        case 'daily':
          const dailyRes = await getDailyReport({ date: dailyDate })
          if (dailyRes.data.success) {
            setDailyData(dailyRes.data.data)
          }
          break

        case 'weekly':
          const weeklyRes = await getWeeklyReport({ week_start: weekStart })
          if (weeklyRes.data.success) {
            setWeeklyData(weeklyRes.data.data)
          }
          break

        case 'member':
          if (selectedMember) {
            const memberRes = await getMemberReport(selectedMember)
            if (memberRes.data.success) {
              setMemberData(memberRes.data.data)
            }
          }
          break
      }
    } catch (err) {
      console.error('Report fetch error:', err)
      setError('Failed to load report data')
    } finally {
      setLoading(false)
    }
  }

  const navigateDaily = (direction) => {
    const current = new Date(dailyDate)
    current.setDate(current.getDate() + direction)
    setDailyDate(current.toISOString().split('T')[0])
  }

  const navigateWeek = (direction) => {
    const current = new Date(weekStart)
    current.setDate(current.getDate() + (direction * 7))
    setWeekStart(current.toISOString().split('T')[0])
  }

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    })
  }

  const formatDateShort = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    })
  }

  return (
    <div className="reports-page">
      {/* Stat Blocks */}
      <div className="stat-blocks">
        <div className="stat-block">
          <div className="stat-icon"><FileText size={20} /></div>
          <div className="stat-content">
            <span className="stat-value">Reports</span>
            <span className="stat-label">Field Team Analytics</span>
          </div>
        </div>
        <div className="stat-block">
          <div className="stat-icon"><BarChart3 size={20} /></div>
          <div className="stat-content">
            <span className="stat-value">{summaryData?.summary?.total_meetings || '-'}</span>
            <span className="stat-label">Total Meetings</span>
          </div>
        </div>
        <div className="stat-block">
          <div className="stat-icon"><Users size={20} /></div>
          <div className="stat-content">
            <span className="stat-value">{summaryData?.summary?.active_members || '-'}</span>
            <span className="stat-label">Active Members</span>
          </div>
        </div>
        <div className="stat-block">
          <div className="stat-icon"><Target size={20} /></div>
          <div className="stat-content">
            <span className="stat-value">{summaryData?.summary?.total_sales || '-'}</span>
            <span className="stat-label">Total Sales</span>
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="reports-toolbar">
        <div className="toolbar-left">
          <div className="view-toggle">
            <button
              className={view === 'summary' ? 'active' : ''}
              onClick={() => setView('summary')}
            >
              Summary
            </button>
            <button
              className={view === 'daily' ? 'active' : ''}
              onClick={() => setView('daily')}
            >
              Daily
            </button>
            <button
              className={view === 'weekly' ? 'active' : ''}
              onClick={() => setView('weekly')}
            >
              Weekly
            </button>
            <button
              className={view === 'member' ? 'active' : ''}
              onClick={() => setView('member')}
            >
              Member
            </button>
          </div>

          {view === 'summary' && (
            <select
              className="filter-select"
              value={summaryPeriod}
              onChange={(e) => setSummaryPeriod(e.target.value)}
            >
              <option value="7">Last 7 days</option>
              <option value="30">Last 30 days</option>
              <option value="90">Last 90 days</option>
            </select>
          )}

          {view === 'daily' && (
            <div className="date-nav">
              <button className="nav-btn" onClick={() => navigateDaily(-1)}>
                <ChevronLeft size={16} />
              </button>
              <input
                type="date"
                value={dailyDate}
                onChange={(e) => setDailyDate(e.target.value)}
                className="date-input"
              />
              <button className="nav-btn" onClick={() => navigateDaily(1)}>
                <ChevronRight size={16} />
              </button>
            </div>
          )}

          {view === 'weekly' && (
            <div className="date-nav">
              <button className="nav-btn" onClick={() => navigateWeek(-1)}>
                <ChevronLeft size={16} />
              </button>
              <span className="week-label">
                Week of {formatDateShort(weekStart)}
              </span>
              <button className="nav-btn" onClick={() => navigateWeek(1)}>
                <ChevronRight size={16} />
              </button>
            </div>
          )}

          {view === 'member' && (
            <select
              className="filter-select"
              value={selectedMember || ''}
              onChange={(e) => setSelectedMember(e.target.value ? parseInt(e.target.value) : null)}
            >
              <option value="">Select Member</option>
              {members.map(m => (
                <option key={m.id} value={m.id}>{m.name}</option>
              ))}
            </select>
          )}
        </div>

        <div className="toolbar-right">
          <button className="btn btn-secondary" onClick={fetchData} disabled={loading}>
            <RefreshCw size={14} className={loading ? 'spin' : ''} />
            Refresh
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="reports-content">
        {loading ? (
          <div className="loading-container">
            <RefreshCw size={24} className="spin" />
            <span>Loading report...</span>
          </div>
        ) : error ? (
          <div className="error-container">
            <span>{error}</span>
            <button className="btn btn-secondary" onClick={fetchData}>
              Retry
            </button>
          </div>
        ) : (
          <>
            {view === 'summary' && summaryData && (
              <SummaryView data={summaryData} />
            )}
            {view === 'daily' && dailyData && (
              <DailyView data={dailyData} />
            )}
            {view === 'weekly' && weeklyData && (
              <WeeklyView data={weeklyData} formatDate={formatDateShort} />
            )}
            {view === 'member' && (
              selectedMember ? (
                memberData ? (
                  <MemberView data={memberData} />
                ) : null
              ) : (
                <div className="empty-state">
                  <User size={48} />
                  <h3>Select a Team Member</h3>
                  <p>Choose a member from the dropdown to view their detailed report.</p>
                </div>
              )
            )}
          </>
        )}
      </div>
    </div>
  )
}

// Summary View Component
function SummaryView({ data }) {
  const { summary, top_performers, period } = data

  return (
    <div className="summary-view">
      <div className="summary-grid">
        <div className="summary-card large">
          <h3>Performance Overview</h3>
          <div className="metrics-grid">
            <div className="metric-box">
              <div className="metric-icon meetings">
                <Calendar size={20} />
              </div>
              <div className="metric-info">
                <span className="metric-value">{summary.total_meetings}</span>
                <span className="metric-label">Meetings</span>
              </div>
            </div>
            <div className="metric-box">
              <div className="metric-icon clients">
                <Users size={20} />
              </div>
              <div className="metric-info">
                <span className="metric-value">{summary.total_new_clients}</span>
                <span className="metric-label">New Clients</span>
              </div>
            </div>
            <div className="metric-box">
              <div className="metric-icon trials">
                <TrendingUp size={20} />
              </div>
              <div className="metric-info">
                <span className="metric-value">{summary.total_trials}</span>
                <span className="metric-label">Trials</span>
              </div>
            </div>
            <div className="metric-box">
              <div className="metric-icon sales">
                <Target size={20} />
              </div>
              <div className="metric-info">
                <span className="metric-value">{summary.total_sales}</span>
                <span className="metric-label">Sales</span>
              </div>
            </div>
          </div>
        </div>

        <div className="summary-card">
          <h3>Team Stats</h3>
          <div className="stat-list">
            <div className="stat-row">
              <span className="stat-label">Total Members</span>
              <span className="stat-value">{summary.total_members}</span>
            </div>
            <div className="stat-row">
              <span className="stat-label">Active Members</span>
              <span className="stat-value">{summary.active_members}</span>
            </div>
            <div className="stat-row">
              <span className="stat-label">Avg Meetings/Day</span>
              <span className="stat-value">{summary.avg_meetings_per_day}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="ranking-section">
        <h3>Top Performers</h3>
        <div className="ranking-table">
          <table>
            <thead>
              <tr>
                <th>Rank</th>
                <th>Member</th>
                <th>Meetings</th>
              </tr>
            </thead>
            <tbody>
              {top_performers.map((performer, idx) => (
                <tr key={performer.member_id}>
                  <td>
                    <span className={`rank-badge rank-${idx + 1}`}>
                      #{idx + 1}
                    </span>
                  </td>
                  <td>{performer.member_name}</td>
                  <td>
                    <span className="meetings-count">{performer.meetings_count}</span>
                  </td>
                </tr>
              ))}
              {top_performers.length === 0 && (
                <tr>
                  <td colSpan="3" className="empty-cell">No data for this period</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

// Daily View Component
function DailyView({ data }) {
  const { date, totals, members } = data

  return (
    <div className="daily-view">
      <div className="daily-header">
        <h3>{new Date(date).toLocaleDateString('en-US', {
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        })}</h3>
      </div>

      <div className="daily-totals">
        <div className="total-card">
          <Calendar size={20} />
          <span className="total-value">{totals.meetings}</span>
          <span className="total-label">Meetings</span>
        </div>
        <div className="total-card">
          <Users size={20} />
          <span className="total-value">{totals.new_clients}</span>
          <span className="total-label">New Clients</span>
        </div>
        <div className="total-card">
          <TrendingUp size={20} />
          <span className="total-value">{totals.trials}</span>
          <span className="total-label">Trials</span>
        </div>
        <div className="total-card">
          <Target size={20} />
          <span className="total-value">{totals.sales}</span>
          <span className="total-label">Sales</span>
        </div>
        <div className="total-card">
          <User size={20} />
          <span className="total-value">{totals.active_members}</span>
          <span className="total-label">Active</span>
        </div>
      </div>

      <div className="members-table-container">
        <table className="reports-table">
          <thead>
            <tr>
              <th>Member</th>
              <th>Meetings</th>
              <th>New Clients</th>
              <th>Trials</th>
              <th>Sales</th>
              <th>Active Hours</th>
              <th>Pings</th>
            </tr>
          </thead>
          <tbody>
            {members.map(m => (
              <tr key={m.member_id}>
                <td className="member-cell">
                  <User size={14} />
                  <span>{m.member_name}</span>
                </td>
                <td>{m.meetings}</td>
                <td>{m.new_clients}</td>
                <td>{m.trials}</td>
                <td>{m.sales}</td>
                <td>{m.active_hours}h</td>
                <td>
                  <span className={`ping-count ${m.location_pings > 0 ? 'active' : 'inactive'}`}>
                    {m.location_pings}
                  </span>
                </td>
              </tr>
            ))}
            {members.length === 0 && (
              <tr>
                <td colSpan="7" className="empty-cell">No activity for this day</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// Weekly View Component
function WeeklyView({ data, formatDate }) {
  const { week_start, week_end, totals, daily_breakdown, member_rankings } = data

  // Simple bar chart calculation
  const maxMeetings = Math.max(...daily_breakdown.map(d => d.meetings), 1)

  return (
    <div className="weekly-view">
      <div className="weekly-header">
        <h3>{formatDate(week_start)} - {formatDate(week_end)}</h3>
      </div>

      <div className="weekly-totals">
        <div className="total-card">
          <Calendar size={20} />
          <span className="total-value">{totals.meetings}</span>
          <span className="total-label">Meetings</span>
        </div>
        <div className="total-card">
          <Users size={20} />
          <span className="total-value">{totals.new_clients}</span>
          <span className="total-label">New Clients</span>
        </div>
        <div className="total-card">
          <TrendingUp size={20} />
          <span className="total-value">{totals.trials}</span>
          <span className="total-label">Trials</span>
        </div>
        <div className="total-card">
          <Target size={20} />
          <span className="total-value">{totals.sales}</span>
          <span className="total-label">Sales</span>
        </div>
      </div>

      <div className="weekly-grid">
        <div className="chart-section">
          <h4>Daily Activity</h4>
          <div className="bar-chart">
            {daily_breakdown.map(day => (
              <div key={day.date} className="bar-column">
                <div className="bar-wrapper">
                  <div
                    className="bar"
                    style={{ height: `${(day.meetings / maxMeetings) * 100}%` }}
                  >
                    <span className="bar-value">{day.meetings}</span>
                  </div>
                </div>
                <span className="bar-label">
                  {new Date(day.date).toLocaleDateString('en-US', { weekday: 'short' })}
                </span>
              </div>
            ))}
            {daily_breakdown.length === 0 && (
              <div className="empty-chart">No data for this week</div>
            )}
          </div>
        </div>

        <div className="rankings-section">
          <h4>Member Rankings</h4>
          <div className="rankings-list">
            {member_rankings.map((m, idx) => (
              <div key={m.member_id} className="ranking-item">
                <span className={`rank rank-${idx + 1}`}>#{idx + 1}</span>
                <span className="member-name">{m.member_name}</span>
                <div className="ranking-stats">
                  <span className="stat">
                    <Calendar size={12} />
                    {m.meetings}
                  </span>
                  <span className="stat">
                    <Users size={12} />
                    {m.new_clients}
                  </span>
                </div>
              </div>
            ))}
            {member_rankings.length === 0 && (
              <div className="empty-rankings">No activity this week</div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

// Member View Component
function MemberView({ data }) {
  const { member, period, totals, daily_activity, recent_meetings, targets } = data

  // Simple bar chart calculation
  const maxMeetings = Math.max(...daily_activity.map(d => d.meetings), 1)

  return (
    <div className="member-view">
      <div className="member-header">
        <div className="member-info">
          <div className="member-avatar">
            <User size={24} />
          </div>
          <div className="member-details">
            <h3>{member.name}</h3>
            <span className="member-username">@{member.username}</span>
          </div>
        </div>
        <div className={`member-status ${member.is_active ? 'active' : 'inactive'}`}>
          {member.is_active ? 'Active' : 'Inactive'}
        </div>
      </div>

      <div className="member-totals">
        <div className="total-card">
          <Calendar size={20} />
          <span className="total-value">{totals.meetings}</span>
          <span className="total-label">Meetings</span>
        </div>
        <div className="total-card">
          <Users size={20} />
          <span className="total-value">{totals.clients}</span>
          <span className="total-label">Clients</span>
        </div>
        <div className="total-card">
          <TrendingUp size={20} />
          <span className="total-value">{totals.trials}</span>
          <span className="total-label">Trials</span>
        </div>
        <div className="total-card">
          <Target size={20} />
          <span className="total-value">{totals.sales}</span>
          <span className="total-label">Sales</span>
        </div>
      </div>

      <div className="member-grid">
        <div className="activity-section">
          <h4>Activity (Last 30 Days)</h4>
          <div className="activity-chart">
            {daily_activity.length > 0 ? (
              <div className="mini-chart">
                {daily_activity.slice(-14).map(day => (
                  <div
                    key={day.date}
                    className="mini-bar"
                    style={{ height: `${Math.max((day.meetings / maxMeetings) * 100, 5)}%` }}
                    title={`${day.date}: ${day.meetings} meetings`}
                  />
                ))}
              </div>
            ) : (
              <div className="empty-chart">No activity data</div>
            )}
          </div>
        </div>

        <div className="targets-section">
          <h4>Recent Targets</h4>
          <div className="targets-list">
            {targets.map(t => (
              <div key={t.id} className={`target-item ${t.status}`}>
                <div className="target-type">{t.period_type}</div>
                <div className="target-progress">
                  <span>{t.target_meetings} meetings target</span>
                  <span className={`target-status ${t.status}`}>
                    {t.status === 'active' ? 'In Progress' : t.status === 'completed' ? 'Completed' : 'Upcoming'}
                  </span>
                </div>
              </div>
            ))}
            {targets.length === 0 && (
              <div className="empty-targets">No targets set</div>
            )}
          </div>
        </div>
      </div>

      <div className="meetings-section">
        <h4>Recent Meetings</h4>
        <table className="reports-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Client</th>
              <th>Status</th>
              <th>Interest</th>
              <th>Outcome</th>
            </tr>
          </thead>
          <tbody>
            {recent_meetings.map(m => (
              <tr key={m.id}>
                <td>{new Date(m.meeting_date).toLocaleDateString()}</td>
                <td>{m.client_name || 'Unknown'}</td>
                <td>
                  <span className={`status-badge ${m.status}`}>
                    {m.status}
                  </span>
                </td>
                <td>
                  <span className={`interest-badge ${m.interest_level}`}>
                    {m.interest_level || '-'}
                  </span>
                </td>
                <td>{m.outcome || '-'}</td>
              </tr>
            ))}
            {recent_meetings.length === 0 && (
              <tr>
                <td colSpan="5" className="empty-cell">No recent meetings</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}
